(() => {
  'use strict';

  const scenes = new Map();
  const active = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let scheduledFrame = null;
  const scrollPosition = () => window.scrollY || document.documentElement.scrollTop || 0;

  const paint = () => {
    scheduledFrame = null;
    const scroll = scrollPosition();
    const anchor = Math.min((window.innerHeight || document.documentElement.clientHeight) * .12, 96);
    const updates = [];
    active.forEach(element => {
      const state = scenes.get(element);
      if (!state?.enabled) return;
      const rect = element.getBoundingClientRect();
      const regionTop = state.region
        ? state.region.getBoundingClientRect().top + (parseFloat(window.getComputedStyle(state.region).paddingTop) || 0)
        : rect.top;
      // Use the non-sticky product region, so reversing scroll restores the entire photograph.
      const start = Math.max(0, scroll + regionTop - anchor, state.readyAtScroll);
      const progress = Math.max(0, Math.min(1, (scroll - start) / Math.max(rect.height * .95, 1)));
      if (Math.abs(progress - state.progress) > .0005) updates.push({ element, state, progress });
    });
    updates.forEach(({ element, state, progress }) => {
      element.style.setProperty('--scene-progress', progress.toFixed(4));
      state.progress = progress;
    });
  };

  const schedule = () => {
    if (scheduledFrame !== null) return;
    for (const element of active) {
      if (!scenes.get(element)?.enabled) continue;
      scheduledFrame = window.requestAnimationFrame(paint);
      break;
    }
  };

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!scenes.has(entry.target)) return;
      entry.target.classList.toggle('coffee-scene--offscreen', !entry.isIntersecting);
      if (entry.isIntersecting) active.add(entry.target);
      else active.delete(entry.target);
    });
    schedule();
  }, { rootMargin: '80px 0px', threshold: 0 }) : null;

  const refreshMotion = (element, state) => {
    state.enabled = element.dataset.motionEnabled !== 'false' && state.intensity > 0 && !reducedMotion.matches && state.ready && !state.failed;
    element.classList.toggle('coffee-scene--motion', state.enabled);
    if (!state.enabled) {
      element.style.setProperty('--scene-progress', '0');
      state.progress = 0;
    }
  };

  const initialize = (root = document) => {
    root.querySelectorAll('[data-coffee-scene]').forEach(element => {
      if (scenes.has(element)) return;
      const suppliedIntensity = Number(element.dataset.motionIntensity);
      const state = {
        enabled: false, failed: false, ready: false, progress: 0, readyAtScroll: scrollPosition(),
        intensity: Number.isFinite(suppliedIntensity) ? Math.max(0, Math.min(100, suppliedIntensity)) : 35,
        region: element.closest?.('.product-page') || null,
        background: element.querySelector('[data-scene-atmosphere]'),
        events: new AbortController()
      };
      scenes.set(element, state);
      element.style.setProperty('--scene-strength', String(state.intensity / 100));
      const backgroundImage = state.background?.querySelector('img');
      let fallbackAttempted = Boolean(backgroundImage?.hasAttribute('data-bundled-scene'));
      const imageReady = () => {
        if (!scenes.has(element) || state.ready || !backgroundImage?.naturalWidth) return;
        state.ready = true;
        state.readyAtScroll = scrollPosition();
        refreshMotion(element, state);
        schedule();
      };
      const backgroundFailure = () => {
        const fallback = state.background?.dataset.sceneFallback;
        if (fallback && !fallbackAttempted) {
          fallbackAttempted = true;
          backgroundImage.removeAttribute('srcset');
          backgroundImage.removeAttribute('sizes');
          backgroundImage.src = fallback;
          return;
        }
        state.failed = true;
        element.classList.add('coffee-scene--art-unavailable');
        refreshMotion(element, state);
      };
      if (backgroundImage) {
        backgroundImage.addEventListener('load', imageReady, { signal: state.events.signal });
        backgroundImage.addEventListener('error', backgroundFailure, { signal: state.events.signal });
        if (backgroundImage.complete) {
          if (backgroundImage.naturalWidth) imageReady();
          else backgroundFailure();
        }
      }
      const main = element.querySelector('[data-scene-main]');
      const mainImage = main?.querySelector('img');
      const pouchFailure = () => {
        main.hidden = true;
        const fallback = element.querySelector('[data-scene-pouch-fallback]');
        if (fallback) fallback.hidden = false;
      };
      if (mainImage) {
        mainImage.addEventListener('error', pouchFailure, { signal: state.events.signal, once: true });
        if (mainImage.complete && !mainImage.naturalWidth) pouchFailure();
      }
      refreshMotion(element, state);
      if (observer) observer.observe(element);
      else active.add(element);
    });
    schedule();
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  reducedMotion.addEventListener?.('change', () => {
    scenes.forEach((state, element) => {
      state.readyAtScroll = scrollPosition();
      refreshMotion(element, state);
    });
    schedule();
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    scenes.forEach((state, element) => {
      if (!event.target.contains(element)) return;
      observer?.unobserve(element);
      state.events.abort();
      active.delete(element);
      scenes.delete(element);
    });
  });
  initialize();
})();
